<script lang="ts">
import { defineComponent } from 'vue';
import { fetchCoupon } from '@/api/admin-be';
import { formatCurrency, formatDate, formatMoney } from '@/utils/format';
import ColoredNumber from '@/components/colored-number.vue';
import Chip from '@/components/chip.vue';
import HiddenBlock from '@/components/hidden-block.vue';
import type { Coupon } from '@/types/coupons';
import { BetSlipResultStatus, BetSlipType } from 'shared-types';
import Multiple from '@/components/calculator/item/multiple.vue';
import ClipboardIcon from '@/components/clipboard-icon.vue';
import { useBetsStore } from '@/stores/bets';
import { mapActions, mapState } from 'pinia';
import Headline from '@/components/headline.vue';
import { useAppStore } from '@/stores/app';

export default defineComponent({
  name: 'calculator-item',
  computed: {
    BetSlipResultStatus() {
      return BetSlipResultStatus;
    },
    BetSlipType() {
      return BetSlipType;
    },
  },
  components: { Headline, ClipboardIcon, Multiple, ColoredNumber, Chip, HiddenBlock },
  data() {
    return {
      coupon: null as null | Coupon,
    };
  },
  methods: {
    formatDate,
    formatMoney,
    formatCurrency,
    ...mapActions(useBetsStore, ['reSettleBet', 'refundBroken', 'repayBroken', 'rollBack']),
    ...mapActions(useAppStore, ['canWriteSection']),
  },
  mounted() {
    fetchCoupon(this.$route.params.id as string).then((data) => (this.coupon = data));
  },
  props: ['id'],
  watch: {
    coupon: {
      handler(v) {
        if (v && v.parentBetSlipId) {
          this.$router.push({
            name: 'calculator-item',
            params: { id: v.parentBetSlipId, stakeId: v.guid || v.id },
          });
        }
      },
    },
    '$route.params.id'(val) {
      if (val) {
        fetchCoupon(val).then((data) => (this.coupon = data));
      }
    },
  },
});
</script>

<template>
  <section v-if="coupon">
    <teleport to="#title">
      <headline class="mx-4 my-2">Детали купона</headline>
    </teleport>

    <header
      class="bg-gray-300 dark:bg-gray-700 p-3 sticky top-0 z-10 shadow-lg shadow-gray-800 mx-3"
    >
      <span class="absolute right-4 top-4 cursor-pointer" @click="$router.go(-1)">
        <i class="pi pi-times" style="font-size: 2em" />
      </span>
      <div class="grid grid-cols-2 gap-x-4 gap-y-1" style="width: max-content">
        <span class="col-span-2">
          Купон
          <chip class="bg-slate-400 dark:bg-slate-500" :value="coupon.id" />
          от
          <chip
            class="bg-slate-400 dark:bg-slate-500"
            :value="formatDate(coupon.createdAt, 'DD.MM.YYYY HH:mm')"
          />
          <span class="text-gray-500 ml-1">
            {{ coupon.guid }}
            <clipboard-icon :text="coupon.guid" />
          </span>
        </span>

        <span>ID пользователя</span>
        <span>
          {{ coupon.userId }}
          <clipboard-icon :text="coupon.userId" />
        </span>

        <span>Валюта</span>
        <span>
          {{ formatCurrency(+coupon.stakeAmount, coupon.currency) }}
        </span>

        <span>Общая ставка</span>
        <span>
          {{ formatMoney(+(coupon.stakeAmount || 0), 2) }}
        </span>
      </div>
    </header>
    <template v-for="(stake, index) in coupon.stakes">
      <section
        :class="{
          'mx-3 my-2 bg-gray-200 dark:bg-gray-800 bets relative': true,
          'border border-red-500': stake.guid === $router.currentRoute.value.params.stakeId,
        }"
      >
        <span class="absolute right-0 mx-1 p-2 text-gray-500" v-if="canWriteSection('bets')">
          <span
            class="cursor-pointer p-1 rounded ml-1 bg-yellow-200 dark:bg-yellow-800 dark:text-gray-800"
            @click="rollBack(stake.guid as string)"
          >
            Откатить
            <i class="pi pi-refresh text-sm mx-1" />
          </span>
          <span
            class="cursor-pointer p-1 rounded ml-1 bg-gray-300 dark:bg-gray-700"
            @click="refundBroken(stake.guid as string)"
          >
            Возврат
            <i class="pi pi-dollar text-sm mx-1" />
          </span>
          <span
            class="cursor-pointer p-1 rounded ml-1 bg-gray-300 dark:bg-gray-700"
            @click="reSettleBet(stake.guid as string)"
          >
            Переотправить в BG
            <i class="pi pi-external-link text-sm mx-1" />
          </span>
          <span
            v-if="stake.status === BetSlipResultStatus.Won"
            class="cursor-pointer p-1 rounded ml-1 bg-red-100 dark:bg-red-900"
            @click="repayBroken(stake.guid as string)"
          >
            Повтор выплаты
            <i class="pi pi-dollar text-sm ml-1" />
            <i class="pi pi-dollar text-sm mr-1" />
          </span>
        </span>
        <multiple :stake="stake" :index="index" :betSlipSelections="coupon.betSlipSelections" />
      </section>
    </template>
  </section>
</template>

<style lang="scss">
.bets section > span {
  @apply py-1 px-4;
}

.calculation {
  & > tbody > tr:first-child td {
    @apply border-t-0;
  }

  & > tbody > tr:last-child td {
    @apply border-b-0;
  }

  & > tbody > tr td:first-child {
    @apply border-l-0;
  }

  & > tbody > tr td:last-child {
    @apply border-r-0;
  }

  td:nth-child(2) {
    @apply text-right;
  }

  td {
    @apply p-1;
  }
}
</style>
